import { useState, useCallback, useRef, useEffect } from 'react';
import { geocodeLocation, fetchTimezone, GeocodingResult } from '@/services/geocodingApi';
import { getCurrentPosition } from '@/services/nativeGeolocationService';

export const useLocationSearch = (initialName: string = '', initialLat: number = 0, initialLon: number = 0) => {
    const [name, setName] = useState(initialName);
    const [lat, setLat] = useState(initialLat);
    const [lon, setLon] = useState(initialLon);
    const [suggestions, setSuggestions] = useState<GeocodingResult['suggestions']>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [gpsError, setGpsError] = useState<string | null>(null);
    const [gpsLoading, setGpsLoading] = useState(false);

    const debounceRef = useRef<NodeJS.Timeout | null>(null);

    const search = useCallback(async (query: string) => {
        if (!query || query.trim().length < 2) {
            setSuggestions([]);
            return;
        }

        setLoading(true);
        setError(null);
        try {
            const result = await geocodeLocation(query);
            if (result.success) {
                setSuggestions(result.suggestions || []);
                // On ne met pas à jour lat/lon ici, on attend la sélection
            } else {
                setError(result.error || 'Erreur lors de la recherche');
            }
        } catch (err) {
            setError('Erreur de connexion');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        if (debounceRef.current) clearTimeout(debounceRef.current);
        if (name && !name.startsWith('GPS:')) {
            debounceRef.current = setTimeout(() => search(name), 600);
        }
        return () => {
            if (debounceRef.current) clearTimeout(debounceRef.current);
        };
    }, [name, search]);

    const useGPS = useCallback(async () => {
        setGpsLoading(true);
        setGpsError(null);

        try {
            const position = await getCurrentPosition();
            const { latitude, longitude } = position;
            setLat(latitude);
            setLon(longitude);
            setName(`GPS:${latitude.toFixed(4)},${longitude.toFixed(4)}`);
            setSuggestions([]);
        } catch (err: any) {
            console.error('GPS Error:', err?.message || err);
            setGpsError(err.message || "Impossible de récupérer votre position.");
        } finally {
            setGpsLoading(false);
        }
    }, []);

    const selectSuggestion = useCallback((suggestion: { lat: number, lon: number, displayName: string }) => {
        setLat(suggestion.lat);
        setLon(suggestion.lon);
        setName(suggestion.displayName);
        setSuggestions([]);
        setError(null);
    }, []);

    return {
        name,
        setName,
        lat,
        lon,
        suggestions,
        loading,
        error,
        gpsError,
        gpsLoading,
        useGPS,
        selectSuggestion
    };
};

